import { Alert, Box, Input, Pill, Select, Stack, useNotifications } from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';
import React, { useState } from 'react';

import { Button } from '../../components/button';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { CompanyTfsSpaceQuery, TfsRegion } from '../../lib/graphql';

export interface CompanyFileStorageViewProps {
  data: CompanyTfsSpaceQuery;
  provisionSpace: (spaceName: string, region: TfsRegion) => Promise<void>;
}

const REGION_OPTIONS = [
  { value: TfsRegion.EU, label: 'EU (Europe)' },
  { value: TfsRegion.US, label: 'US (United States)' },
];

export const CompanyFileStorageView: React.FC<CompanyFileStorageViewProps> = ({ data, provisionSpace }) => {
  const company = data.company;
  const tfsSpace = company?.tfsSpace;
  const [spaceName, setSpaceName] = useState('');
  const [region, setRegion] = useState<TfsRegion>(TfsRegion.EU);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const notify = useNotifications();

  if (isNil(company)) {
    return <div>Company not found</div>;
  }

  const handleProvision = async () => {
    if (!spaceName.trim()) {
      setError('Please enter a space name');
      return;
    }

    setIsProvisioning(true);
    setError(null);
    setSuccess(false);

    try {
      await provisionSpace(spaceName.trim(), region);
      setSuccess(true);
      setSpaceName('');
      setRegion(TfsRegion.EU);
      notify.success({
        title: 'Space Provisioned Successfully',
        message: 'Company file storage space has been created. Users can now upload documents.',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to provision TFS space';
      setError(errorMessage);
      notify.error({ title: 'Provisioning Failed', message: errorMessage });
    } finally {
      setIsProvisioning(false);
    }
  };

  return (
    <div>
      {tfsSpace ? (
        <>
          <Alert kind="success">
            <strong>TFS Space Provisioned</strong>
            <p>This company has a Trimble File Service space configured.</p>
          </Alert>

          <Box
            style={{
              padding: '1em',
              border: '1px solid rgba(34,36,38,.15)',
              borderRadius: '.28571429rem',
              marginTop: '1em',
            }}>
            <Stack direction="row" gap="medium">
              <Box style={{ flex: 1 }}>
                <h4>Space Details</h4>
                <Box
                  style={{
                    border: '1px solid rgba(34,36,38,.15)',
                    borderRadius: '.28571429rem',
                    overflow: 'hidden',
                  }}>
                  <div style={{ padding: '0.875em 1em', borderBottom: '1px solid rgba(34,36,38,.1)' }}>
                    <strong>Space Name</strong>
                    <div>{tfsSpace.spaceName}</div>
                  </div>
                  <div style={{ padding: '0.875em 1em', borderBottom: '1px solid rgba(34,36,38,.1)' }}>
                    <strong>Space ID</strong>
                    <div>
                      <code>{tfsSpace.spaceId}</code>
                    </div>
                  </div>
                  <div style={{ padding: '0.875em 1em', borderBottom: '1px solid rgba(34,36,38,.1)' }}>
                    <strong>Root Folder ID</strong>
                    <div>
                      <code>{tfsSpace.rootFolderId}</code>
                    </div>
                  </div>
                  {tfsSpace.region && (
                    <div style={{ padding: '0.875em 1em', borderBottom: '1px solid rgba(34,36,38,.1)' }}>
                      <strong>Region</strong>
                      <div>
                        <Pill kind="info">{tfsSpace.region.toUpperCase()}</Pill>
                      </div>
                    </div>
                  )}
                  <div style={{ padding: '0.875em 1em', borderBottom: '1px solid rgba(34,36,38,.1)' }}>
                    <strong>Account ID</strong>
                    <div>
                      <code>{tfsSpace.accountId}</code>
                    </div>
                  </div>
                  <div style={{ padding: '0.875em 1em' }}>
                    <strong>Created At</strong>
                    <div>
                      <FormattedDate date={tfsSpace.createdAt} />
                    </div>
                  </div>
                </Box>
              </Box>
              <Box style={{ flex: 1 }}>
                <h4>Information</h4>
                <Alert kind="info">
                  <div>
                    <strong>About TFS Spaces</strong>
                    <ul style={{ paddingLeft: '1em', marginTop: '0.5em' }}>
                      <li>TFS spaces store all documents uploaded by this company&apos;s users</li>
                      <li>Files are organized into tour-specific folders</li>
                      <li>Space cannot be deleted (only via Trimble support)</li>
                      <li>Users must have a provisioned space before uploading files</li>
                    </ul>
                  </div>
                </Alert>
              </Box>
            </Stack>
          </Box>
        </>
      ) : (
        <>
          <Alert kind="warning">
            <strong>No file storage space created yet</strong>
            <p>
              This company does not have a File storage space yet. Users will not be able to upload documents until a
              space is created.
            </p>
          </Alert>

          <Box
            style={{
              padding: '1em',
              border: '1px solid rgba(34,36,38,.15)',
              borderRadius: '.28571429rem',
              marginTop: '1em',
            }}>
            <h3>Create file storage space</h3>
            <p>Once created, users can upload documents via the customer portal.</p>

            <div>
              <div style={{ marginBottom: '1em' }}>
                <Input
                  label="Space Name"
                  labelPosition="block"
                  name="space-name"
                  required
                  placeholder={`${company?.company_name ?? 'Company'} - file storage`}
                  value={spaceName}
                  onChange={({ value }) => setSpaceName(value)}
                  disabled={isProvisioning}
                />
                <small style={{ color: 'var(--color-neutral-text-subtle)' }}>
                  Choose a descriptive name for this company&apos;s file storage space
                </small>
              </div>

              <div style={{ marginBottom: '1em' }}>
                <Select
                  label="Region"
                  labelPosition="block"
                  required
                  options={REGION_OPTIONS}
                  value={region}
                  onChange={(option) => {
                    if (option !== null) {
                      setRegion(option.value as TfsRegion);
                    }
                  }}
                  disabled={isProvisioning}
                  placeholder="Select region"
                />
                <small style={{ color: 'var(--color-neutral-text-subtle)' }}>
                  Select the geographic region where files will be stored (defaults to EU)
                </small>
              </div>

              {error && (
                <Alert kind="danger">
                  <strong>Provisioning Failed</strong>
                  <p>{error}</p>
                </Alert>
              )}

              {success && (
                <Alert kind="success">
                  <strong>Space Provisioned Successfully!</strong>
                  <p>Company file storage space has been created. Users can now upload documents.</p>
                </Alert>
              )}

              <Stack>
                <Button
                  kind="primary"
                  onClick={handleProvision}
                  disabled={isProvisioning || !spaceName.trim()}
                  loading={isProvisioning}>
                  {isProvisioning ? 'Provisioning...' : 'Provision Space'}
                </Button>
              </Stack>
            </div>
          </Box>
        </>
      )}
    </div>
  );
};
